import React, { useMemo } from 'react';
import { motion, type Variants } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, BookOpen, Compass, Sparkles, Users } from 'lucide-react';

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.75 } },
} satisfies Variants;

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1, delayChildren: 0.05 } },
} satisfies Variants;

const PAGE_SURFACE = '#f4f7fc';

const About: React.FC = () => {
  const navigate = useNavigate();

  const assets = useMemo(
    () => ({
      hero: '/assets/seminar-photo/DSC_0514.jpg',
      faraz: '/assets/faraz.jpg',
      gallery: [
        '/assets/sliders/Header-Site-1.jpg',
        '/assets/seminar-photo/IMG_4674.JPG',
        '/assets/seminar-photo/IMG_4676.JPG',
        '/assets/seminar-photo/DSC00117.jpg',
      ],
    }),
    [],
  );

  const focusAreas = [
    {
      title: 'خودشناسی و ناخودآگاه',
      desc: 'شناخت لایه‌های عمیق ذهن، بازنویسی الگوها و ساختن یک مسیر روشن برای رشد.',
    },
    {
      title: 'روابط عاطفی',
      desc: 'ارتباط سالم، مرزهای درست و مهارت‌هایی برای ساختن رابطه‌های امن و بالغ.',
    },
    {
      title: 'روابط مالی',
      desc: 'تغییر نگاه به پول، تصمیم‌گیری هوشمندانه و ساختن مسیر پایدار مالی.',
    },
    {
      title: 'معنا و توسعه فردی',
      desc: 'رشد درونی، هدف‌مندی و تبدیل شدن به نسخه‌ای که همیشه منتظرش بودی.',
    },
  ];

  return (
    <div className="min-h-screen pb-16 pt-4 text-[#0f172a]" style={{ backgroundColor: PAGE_SURFACE }} dir="rtl">
      <div className="mx-auto w-full max-w-[min(1220px,96vw)] px-3 sm:px-5 lg:px-6">
        <nav
          aria-label="مسیر دسترسی"
          className="mb-3 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[11px] text-slate-500 sm:text-xs"
        >
          <Link to="/" className="font-medium transition hover:text-[#3A68C2]">
            خانه
          </Link>
          <span className="text-slate-400 opacity-80">›</span>
          <span className="font-semibold text-slate-700">درباره ما</span>
        </nav>

        <motion.section
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative overflow-hidden rounded-2xl border border-slate-200/85 bg-white shadow-[0_2px_28px_-14px_rgba(58,104,194,0.18),0_8px_32px_-24px_rgba(15,23,42,0.08)] sm:rounded-[1.25rem]"
        >
          <div className="absolute inset-0">
            <img src={assets.hero} alt="درباره ما" className="h-full w-full object-cover opacity-[0.13]" />
            <div className="absolute inset-0 bg-gradient-to-br from-[#f9fbff] via-white to-[#eef4ff]" />
            <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-[#3A68C2]/10 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-20 -left-10 h-56 w-56 rounded-full bg-indigo-200/45 blur-3xl" />
          </div>
          <div className="relative px-4 py-5 sm:px-6 sm:py-6">
            <motion.div variants={stagger} initial="hidden" animate="visible" className="space-y-4 sm:space-y-5">
              <motion.div variants={fadeUp} className="inline-flex items-center gap-2 rounded-full bg-[#3A68C2]/10 px-3 py-1">
                <Sparkles className="h-3.5 w-3.5 text-[#3A68C2]" />
                <span className="text-[11px] font-bold text-[#2f5599] sm:text-xs">درباره موسسه من حقیقی</span>
              </motion.div>
              <motion.h1 variants={fadeUp} className="text-[1.35rem] font-extrabold leading-tight text-[#0f172a] sm:text-2xl md:text-3xl">
                داستان <span className="text-violet-600">رشد</span>، آموزش و
                <span className="text-violet-600"> تحول</span> در من حقیقی
              </motion.h1>
              <motion.p variants={fadeUp} className="max-w-3xl text-sm leading-7 text-slate-600 sm:text-[15px]">
                هشت سال فعالیت مستمر در مسیر آموزش معنا، خودشناسی و توسعه فردی با همراهی مخاطبانی از ایران و جهان.
              </motion.p>
              <motion.div variants={fadeUp} className="grid gap-2.5 sm:max-w-2xl sm:grid-cols-3">
                {[
                  { label: 'مدت فعالیت', value: '۸ سال مستمر' },
                  { label: 'برنامه های آموزشی', value: '۲۰۰۰+ کارگاه' },
                  { label: 'همراهان مجموعه', value: '۲۰۰هزار+ نفر' },
                ].map((item) => (
                  <div key={item.label} className="rounded-xl border border-slate-200/90 bg-white/85 px-3 py-2.5 shadow-sm backdrop-blur">
                    <p className="text-[10px] font-semibold text-slate-500">{item.label}</p>
                    <p className="mt-1 text-xs font-extrabold text-[#1e3a8a] sm:text-[13px]">{item.value}</p>
                  </div>
                ))}
              </motion.div>
              <motion.div variants={fadeUp} className="flex flex-wrap items-center gap-2.5 sm:gap-3">
                <button
                  onClick={() => navigate('/contact')}
                  className="inline-flex items-center gap-2 rounded-xl bg-[#3A68C2] px-4 py-2.5 text-xs font-bold text-white shadow-sm transition hover:bg-[#345daf] sm:px-5 sm:py-3 sm:text-sm"
                >
                  ارتباط با ما
                  <ArrowLeft className="h-4 w-4" />
                </button>
                <button
                  onClick={() => navigate('/courses')}
                  className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-xs font-semibold text-slate-700 transition hover:border-[#3A68C2]/35 hover:text-[#2f5599] sm:px-5 sm:py-3 sm:text-sm"
                >
                  مشاهده دوره ها
                </button>
              </motion.div>
            </motion.div>
          </div>
        </motion.section>

        <main className="mt-5 grid gap-5 lg:grid-cols-[minmax(0,1fr),23rem]">
          <motion.section
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.18 }}
            className="space-y-5"
          >
            <motion.div variants={fadeUp} className="rounded-2xl border border-slate-200/85 bg-white p-5 shadow-sm sm:p-6">
              <h2 className="text-lg font-extrabold text-[#0f172a] sm:text-xl">
                <span className="text-violet-600">داستان</span> ما
              </h2>
              <p className="mt-3 text-sm leading-7 text-slate-600 sm:text-[15px]">
                موسسه آموزشی من حقیقی با مدیریت فراز قورچیان، بیش از هشت سال است در حوزه آموزش معنا، خودشناسی،
                توسعه فردی و روابط عاطفی و مالی فعالیت می کند و توانسته میزبان مخاطبان گسترده ای از سراسر ایران و جهان باشد.
              </p>
              <p className="mt-3 text-sm leading-7 text-slate-600 sm:text-[15px]">
                این مجموعه با اجرای بیش از دو هزار کارگاه و همراهی بیش از دویست هزار شرکت کننده، مسیر یادگیری عمیق
                و تجربه محور را دنبال می کند تا شرکت کنندگان در زندگی شخصی و حرفه ای خود تغییر واقعی ایجاد کنند.
              </p>
              <p className="mt-3 text-sm leading-7 text-slate-600 sm:text-[15px]">
                مخاطبان من حقیقی با بهره گیری از دوره ها و کارگاه های تخصصی توانسته اند بر چالش های روانی، مالی و عاطفی
                خود غلبه کنند و مسیر تازه ای برای زندگی شان بسازند.
              </p>
            </motion.div>

            <motion.div variants={fadeUp} className="grid gap-3 sm:grid-cols-3">
              {[
                { value: '۸+', label: 'سال فعالیت', Icon: Sparkles },
                { value: '۲۰۰۰+', label: 'کارگاه آموزشی', Icon: BookOpen },
                { value: '۲۰۰K+', label: 'شرکت کننده', Icon: Users },
              ].map((item) => (
                <div key={item.label} className="rounded-xl border border-slate-200/90 bg-white px-4 py-5 text-center shadow-sm">
                  <div className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-lg bg-[#3A68C2]/11 ring-1 ring-[#3A68C2]/25">
                    <item.Icon className="h-4 w-4 text-[#2f5599]" />
                  </div>
                  <p className="text-xl font-black text-[#0f172a]">{item.value}</p>
                  <p className="mt-1 text-xs font-semibold text-slate-500">{item.label}</p>
                </div>
              ))}
            </motion.div>

            <motion.div variants={fadeUp} className="rounded-2xl border border-slate-200/85 bg-white p-3 shadow-sm sm:p-4">
              <p className="mb-3 px-1 text-xs font-semibold text-[#3A68C2]">لحظه هایی از همایش ها و سمینارها</p>
              <div className="grid gap-3 sm:grid-cols-3">
                {assets.gallery.slice(0, 3).map((src) => (
                  <div key={src} className="overflow-hidden rounded-xl border border-slate-200">
                    <img src={src} alt="گالری من حقیقی" className="h-44 w-full object-cover sm:h-40" />
                  </div>
                ))}
              </div>
            </motion.div>
          </motion.section>

          <motion.aside
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.18 }}
            className="space-y-4 lg:sticky lg:top-28 lg:self-start"
          >
            <motion.div variants={fadeUp} className="rounded-2xl border border-slate-200/90 bg-white p-5 shadow-sm">
              <div className="flex items-center gap-3">
                <img src={assets.faraz} alt="فراز قورچیان" className="h-14 w-14 rounded-xl border border-slate-200 object-cover" />
                <div>
                  <p className="text-sm font-bold text-[#0f172a]">فراز قورچیان</p>
                  <p className="text-xs text-slate-500">موسس، مدیرعامل و راهبر مجموعه</p>
                </div>
              </div>
              <p className="mt-4 text-sm leading-7 text-slate-600">
                فراز قورچیان به عنوان محقق، مدرس و سخنران حوزه خودآگاهی و توسعه فردی، مسیر رشد مخاطبان را با رویکردهای آموزشی
                کاربردی و تجربه های واقعی همراهی می کند.
              </p>
            </motion.div>

            <motion.div variants={fadeUp} className="rounded-2xl border border-slate-200/90 bg-white p-5 shadow-sm">
              <p className="text-xs font-semibold text-[#3A68C2]">محورهای کلیدی</p>
              <div className="mt-3 space-y-2.5">
                {focusAreas.map((item) => (
                  <div key={item.title} className="rounded-xl border border-slate-200 bg-slate-50/70 p-3">
                    <div className="flex items-center gap-2">
                      <Compass className="h-3.5 w-3.5 text-violet-600" />
                      <p className="text-xs font-extrabold text-[#0f172a]">{item.title}</p>
                    </div>
                    <p className="mt-1.5 text-xs leading-6 text-slate-600">{item.desc}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </motion.aside>
        </main>

        <motion.section
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          className="mt-5 overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6"
        >
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-xs font-semibold text-[#3A68C2]">هم مسیری با من حقیقی</p>
              <h2 className="mt-1 text-lg font-extrabold text-[#0f172a] sm:text-xl">
                چه حضوری، چه آنلاین - شما هم بخشی از
                <span className="text-violet-600"> داستان ما</span> هستید
              </h2>
              <p className="mt-2 text-sm leading-7 text-slate-600">
                اگر آماده تغییر و تحول هستید، با دوره ها، کارگاه ها و برنامه های آموزشی من حقیقی همراه شوید.
              </p>
            </div>
            <div className="flex flex-wrap gap-2.5">
              <button
                onClick={() => navigate('/contact')}
                className="inline-flex items-center gap-2 rounded-xl bg-[#3A68C2] px-4 py-2.5 text-xs font-bold text-white transition hover:bg-[#345daf] sm:text-sm"
              >
                تماس با ما
                <ArrowLeft className="h-4 w-4" />
              </button>
              <button
                onClick={() => navigate('/courses')}
                className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-xs font-semibold text-slate-700 transition hover:border-[#3A68C2]/35 hover:text-[#2f5599] sm:text-sm"
              >
                مشاهده دوره ها
              </button>
            </div>
          </div>
        </motion.section>
      </div>
    </div>
  );
};

export default About;
